/**
 * Shared Animation Constants
 *
 * Framer Motion animation variants used across the Lightive website.
 * Extracted from multiple page components to ensure consistency.
 *
 * Note: These constants are typed loosely to maintain compatibility
 * with different versions of framer-motion across packages.
 */

// =============================================================================
// EASING FUNCTIONS
// =============================================================================

export const EASING = {
  default: [0.4, 0, 0.2, 1] as const,
  bounce: [0.68, -0.55, 0.265, 1.55] as const,
  smooth: [0.25, 0.1, 0.25, 1] as const,
};

// =============================================================================
// DURATION CONSTANTS
// =============================================================================

export const DURATION = {
  fast: 0.15,
  normal: 0.25,
  slow: 0.4,
  page: 0.25,    // Optimized for snappy page transitions (was 0.5)
  stagger: 0.05, // Faster stagger for smoother grid animations (was 0.1)
};

// =============================================================================
// COMMON ANIMATION VARIANTS
// =============================================================================

/**
 * Stagger container - animates children with staggered delays
 * Use with AnimatePresence and motion.div
 */
export const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: DURATION.stagger,
      delayChildren: 0.2,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      staggerChildren: 0.05,
      staggerDirection: -1,
    },
  },
};

/**
 * Fade in with upward movement
 * Standard entrance animation for content blocks
 */
export const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: DURATION.page,
      ease: EASING.smooth,
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: DURATION.normal,
      ease: EASING.default,
    },
  },
};

/**
 * Fade in with downward movement
 * Used for dropdown menus and overlays
 */
export const fadeInDown = {
  hidden: {
    opacity: 0,
    y: -20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: DURATION.normal,
      ease: EASING.smooth,
    },
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: {
      duration: DURATION.fast,
    },
  },
};

/**
 * Scale fade animation
 * Used for modals and dialogs
 */
export const scaleFade = {
  hidden: {
    opacity: 0,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: DURATION.normal,
      ease: EASING.smooth,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: {
      duration: DURATION.fast,
    },
  },
};

/**
 * Slide in from left
 * Used for sidebars and navigation panels
 */
export const slideInLeft = {
  hidden: {
    x: -100,
    opacity: 0,
  },
  visible: {
    x: 0,
    opacity: 1,
    transition: {
      duration: DURATION.normal,
      ease: EASING.smooth,
    },
  },
  exit: {
    x: -100,
    opacity: 0,
    transition: {
      duration: DURATION.fast,
    },
  },
};

/**
 * Slide in from right
 * Used for side panels
 */
export const slideInRight = {
  hidden: {
    x: 100,
    opacity: 0,
  },
  visible: {
    x: 0,
    opacity: 1,
    transition: {
      duration: DURATION.normal,
      ease: EASING.smooth,
    },
  },
  exit: {
    x: 100,
    opacity: 0,
    transition: {
      duration: DURATION.fast,
    },
  },
};

// =============================================================================
// GRID ITEM ANIMATIONS
// =============================================================================

/**
 * Grid item animation for portfolio cards
 * Supports sibling hover opacity effect
 */
export const gridItemVariants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: DURATION.slow,
      ease: EASING.smooth,
    },
  },
};

/**
 * Create grid item animation state based on hover
 * @param isHovered - Whether this item is hovered
 * @param isSibling - Whether a sibling item is hovered
 */
export function getGridItemAnimateState(isHovered: boolean, isSibling: boolean) {
  return {
    opacity: isSibling ? 0.5 : 1,
    y: 0,
    scale: isHovered ? 1.02 : 1,
  };
}

// =============================================================================
// TRANSITION PRESETS
// =============================================================================

export const transitions = {
  default: {
    duration: DURATION.normal,
    ease: EASING.default,
  },
  smooth: {
    duration: DURATION.page,
    ease: EASING.smooth,
  },
  bounce: {
    duration: DURATION.normal,
    ease: EASING.bounce,
  },
  spring: {
    type: 'spring' as const,
    stiffness: 300,
    damping: 30,
  },
};

// =============================================================================
// HOVER ANIMATIONS
// =============================================================================

export const hoverScale = {
  scale: 1.02,
  transition: transitions.default,
};

export const hoverLift = {
  y: -4,
  transition: transitions.default,
};

export const imageHoverZoom = {
  scale: 1.05,
  transition: {
    duration: DURATION.slow,
    ease: EASING.smooth,
  },
};
